import React from 'react';
import type { PropFirm } from '../types';

const CATEGORY_FILTERS = [
  { key: 'evaluation', label: 'Evaluation Type' },
  { key: 'platforms', label: 'Platforms' },
  { key: 'payout', label: 'Payout Frequency' },
  { key: 'drawdown', label: 'Daily Drawdown' },
  { key: 'profitSplit', label: 'Profit Split' },
  { key: 'maxFunding', label: 'Max Funding' }
];

type FilterState = {
  search: string;
  evaluation: string[];
  platforms: string[];
  payout: string[];
  drawdown: string[];
  profitSplit: string[];
  maxFunding: string[];
};

type Props = {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  firms: PropFirm[];
};

export const FilterBar: React.FC<Props> = ({ filters, onChange, firms }) => {
  const toggleFilter = (category: keyof FilterState, value: string) => {
    const newValues = filters[category].includes(value)
      ? filters[category].filter((v) => v !== value)
      : [...filters[category], value];

    onChange({ ...filters, [category]: newValues });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, search: e.target.value });
  };

  const clearFilters = () => {
    onChange({ search: '', evaluation: [], platforms: [], payout: [], drawdown: [], profitSplit: [], maxFunding: [] });
  };

  const getUniqueValues = (getter: (firm: PropFirm) => string[]) => {
    return Array.from(new Set(firms.flatMap(getter))).sort();
  };

  return (
    <div className="bg-gray-950 border-b border-white/10 py-4 px-4 md:px-0">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="Search firm..."
          value={filters.search}
          onChange={handleSearch}
          className="w-full px-4 py-2 rounded-lg bg-white/10 text-yellow-100 placeholder-yellow-400 outline-none focus:ring-2 focus:ring-yellow-400"
        />

        {CATEGORY_FILTERS.map(({ key, label }) => (
          <div key={key} className="flex flex-wrap gap-2 items-center text-yellow-300 text-sm">
            <span className="text-yellow-500 font-medium min-w-[130px]">{label}:</span>
            {getUniqueValues((firm) => {
              switch (key) {
                case 'evaluation': return firm.evaluation?.types || [];
                case 'platforms': return firm.features?.platforms || [];
                case 'payout': return firm.features?.payout ? [firm.features.payout] : [];
                case 'drawdown': return firm.features?.maxDrawdown ? [firm.features.maxDrawdown.split(',')[0].trim()] : [];
                case 'profitSplit': return firm.features?.profitSplit ? [firm.features.profitSplit] : [];
                case 'maxFunding': return firm.features?.maxFunding ? [firm.features.maxFunding] : [];
                default: return [];
              }
            }).map((item) => (
              <button
                key={item}
                onClick={() => toggleFilter(key as keyof FilterState, item)}
                className={`px-3 py-1 rounded-full border text-xs transition ${filters[key].includes(item) ? 'bg-yellow-400 text-black border-yellow-400' : 'bg-white/10 text-yellow-300 border-white/20 hover:bg-white/20'}`}
              >
                {item}
              </button>
            ))}
          </div>
        ))}

        {(filters.search || CATEGORY_FILTERS.some(({ key }) => filters[key].length > 0)) && (
          <button onClick={clearFilters} className="text-yellow-300 text-sm hover:underline mt-2">
            Clear All Filters
          </button>
        )}
      </div>
    </div>
  );
};